import React, { useRef, useState, useEffect, useCallback } from 'react';
import DataGrid, { Column, DataGridTypes, Editing, Scrolling, Summary, TotalItem } from 'devextreme-react/data-grid';
import { GetOldGoldPurchaseOrderItemListByBagId, GetTodayMayaveGoldRate, UpdateMayavePurchaseOrderRate } from '../servises';
import { Button } from 'devextreme-react/button';
import notistack from 'modules/core/notistack';
import { HttpStatus } from 'modules/core';

interface ShowOldGoldRequestModalProp {
    selectedId: string;
    handleClose(): void;
    reloadDataSource(): void;
    status: string;
}

const ShowOldGoldRequestModal: React.FC<ShowOldGoldRequestModalProp> = ({ selectedId, handleClose, reloadDataSource, status }) => {
    const dataGridRef = useRef<DataGrid>(null);
    const [gridData, setGridData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const activeInputsRef = useRef<Map<string, HTMLInputElement>>(new Map());
    let setUpdatedMayavePurity: number[] = [0];

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const response = await GetOldGoldPurchaseOrderItemListByBagId(selectedId);
                const data = Array.isArray(response) ? response : response.data || [];
                const updatedData = data.map((item: any, i: number) => ({
                    ...item,
                    todayMayaveGoldRate: response.data[i].rate
                }));

                setGridData(updatedData);

            } catch (error) {
                console.error("Data loading error:", error);
                setGridData([]);
            } finally {
                setLoading(false);
            }
        };

        if (selectedId) {
            loadData();
        }
    }, [selectedId]);



    const isMainStore = localStorage.getItem('mayavePurityEdit') !== "false";

    const cellTemplate = useCallback((cellElement: HTMLElement, cellInfo: any) => {

        const isEditable = isMainStore && status.toLowerCase() === 'PENDING'.toLowerCase()
        if (!isEditable) {
            cellElement.innerHTML = `<span style="color: gray;">${cellInfo.text}</span>`;
            return;
        }
        let inputElement = document.createElement('input');
        inputElement.classList.add('form-control');
        inputElement.type = 'number';
        inputElement.step = '0.01';
        inputElement.min = '0';
        inputElement.max = '100';
        inputElement.value = cellInfo.value || '0';

        const performCalculations = () => {

            const mayavePurity = parseFloat(inputElement.value) || 0;
            if (isNaN(mayavePurity) || mayavePurity < 0) {
                notistack.error("Mayave Purity cannot be negative.");
                inputElement.value = "0";
                return;
            }

            if (mayavePurity > 100) {
                notistack.error("Mayave Purity cannot be greater than 100.");
                inputElement.value = "100";
                return;
            }
            setUpdatedMayavePurity.push(...setUpdatedMayavePurity, mayavePurity);
            const currentData = dataGridRef.current?.instance?.getDataSource().items();
            const currentRow = currentData?.find((item: any) => item.id === cellInfo.data.id);

            if (currentRow) {
                const newCalculatedWeight = Number(currentRow.netWeight * mayavePurity) / 100;
                const { todayMayaveGoldRate, rate } = currentRow;

                const usedGoldRate = todayMayaveGoldRate ?? rate ?? todayMayaveGoldRate;
                const todayGoldRate = usedGoldRate!;
                const finalAmount = Number(newCalculatedWeight * todayGoldRate);

                // dataGridRef.current?.instance.cellValue(cellInfo.rowIndex, 'mayavePurity', mayavePurity);

                dataGridRef.current?.instance.cellValue(cellInfo.rowIndex, 'calculatedWeight', newCalculatedWeight.toFixed(3));
                dataGridRef.current?.instance.cellValue(cellInfo.rowIndex, 'finalAmount', finalAmount);

                dataGridRef.current?.instance.saveEditData();
                // setnewEnteredPurity(mayavePurity => mayavePurity)


            }
        };



        // Handle Enter key press
        inputElement.addEventListener('keydown', (e) => {
            if (e.key === '-') {
                e.preventDefault();
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                performCalculations();
                inputElement.blur();
            }
        });

        cellElement.appendChild(inputElement);

    }, []);

    useEffect(() => {
        return () => {
            activeInputsRef.current.clear();
        };
    }, []);

    if (loading) {
        return <div>Loading...</div>;
    }

    const handleSubmit = async () => {
        try {
            const finalAmount = gridData.reduce(
                (sum, item) => sum + (Number(item.finalAmount) || 0), 0);
            // console.log(gridData)

            const filteredUpdatedPurity = setUpdatedMayavePurity.slice(-gridData.length);

            const data = {
                'BagId': selectedId,
                'FinalPrice': finalAmount,
                'PurchaseorderItemList': gridData.map((item, i) => ({
                    'PurchaseOrderItemId': item.PurchaseOrderItemId || item.id || 0,
                    'MayavePurity': filteredUpdatedPurity[i],
                    'MayaveEquivalentWeight': item.calculatedWeight,
                    'MayaveGoldRate': item.todayMayaveGoldRate ?? 0,
                    'FinalAmount': item.finalAmount ?? 0,
                })),
            };


            const response = await UpdateMayavePurchaseOrderRate(data);
            if (response.status === HttpStatus.Success) {
                notistack.notifyFromApi(response);
                handleClose()
                reloadDataSource()
            }
            else {
                notistack.notifyFromApi(response);

            }
        } catch (error: any) {
            notistack.notifyFromApi(error.message);

        }
    }

    return (
        <>

            <DataGrid
                dataSource={gridData}
                keyExpr="id"
                ref={dataGridRef}
                showBorders={true}
                columnAutoWidth={true}
                columnHidingEnabled={false}
                wordWrapEnabled={false}
                repaintChangesOnly={true}
            >
                <Scrolling
                    mode='standard'
                    useNative={true}
                    scrollByContent={true}
                    scrollByThumb={true}
                />

                <Column dataField="karat" caption="Karat" dataType="string" allowEditing={false} />
                <Column dataField="rate" caption="Rate" format="#,##0.00" dataType="number" allowEditing={false} />
                <Column dataField="purity" caption="Purity" dataType="number" allowEditing={false} />
                <Column dataField="netWeight" caption="Net Weight" format="#,##0.000" dataType="number" allowEditing={false} />
                <Column dataField="equivalentWeight" caption="Eq Wt" format="#,##0.000" dataType="number" allowEditing={false} />
                <Column dataField="purchaseAmount" format="#,##0.00" caption="Purchase Amount" dataType="number" allowEditing={false} />

                <Column dataField="todayMayaveGoldRate" format="#,##0.00" caption="Mayave Gold Rate" dataType="number" allowEditing={false} />

                <Column
                    dataField="mayavePurity"
                    caption="Mayave Purity"
                    dataType="number"
                    cellTemplate={cellTemplate}
                    format="#,##0.00"
                    allowEditing={false}
                />

                <Column
                    dataField="calculatedWeight"
                    caption="Mayave Eq Wt"
                    dataType="number"
                    format="#,##0.000"
                    allowEditing={false}
                />

                <Column
                    dataField="finalAmount"
                    caption="Final Amount"
                    width={200}
                    dataType="number"
                    allowEditing={false}
                    format="#,##0.00"
                />

                <Summary>
                    {/* <TotalItem column="mayavePurity" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 2 }}
                        displayFormat="Total: {0}" /> */}
                    <TotalItem column="calculatedWeight" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 2 }}
                        displayFormat="Total Eq Wt: {0}" />
                    <TotalItem column="finalAmount" summaryType="sum" valueFormat={{ type: 'fixedPoint', precision: 2 }}
                        displayFormat="Total Amt: {0}" />
                </Summary>

            </DataGrid>

            {isMainStore && status?.toLowerCase() === 'PENDING'.toLowerCase() ?
                (<div className="d-flex justify-content-end w-100 mt-3">
                    <Button
                        text='Accept'
                        className='submit-btn'
                        onClick={handleSubmit}
                    />
                </div>) : null}
        </>
    );
};

export default ShowOldGoldRequestModal;